/**
 * The card hand on the AOTD page: what the Deck look draws, built from
 * `GET /cards/my-cards` (tiles with server playability, the in_hand
 * instances, offers, the hand of 4 and the play window). The rules live on
 * the server (modules/aotd/quiz/CONTRACT.md in the backend):
 * - a hand holds HAND_SIZE cards; a new card into a full hand is an offer to
 *   keep (throwing one) or throw away
 * - cards play from the day's unlock to unlock + 45 minutes
 * - Reclamation asks for its emojis (up to 10) when it is played
 * - Second Guess opens one more lock in today's quiz
 */
import { isGameCard } from "../lib/cardState";
import type {
  CardDefinition,
  CardInstance,
  CardRarity,
  GetMyCardsResponse,
  OwnedCardTile,
  Playability,
} from "../types/cardTypes";
import { HAND_SIZE, PLAY_WINDOW_MS } from "../types/handTypes";

export const DECK_SIZE = HAND_SIZE;
export { PLAY_WINDOW_MS };

export type Rarity = CardRarity;

/** What a card face shows. */
export interface HandCardDef {
  slug: string;
  emoji: string;
  name: string;
  rarity: Rarity;
  text: string;
  /** When the effect lands, said plainly. */
  when: string;
  isNew?: boolean;
}

/** One in_hand copy. */
export interface HandEntry {
  instanceId: string;
  card: HandCardDef;
  definition: CardDefinition;
  /** The tile's playability (copies of one card share it). */
  playability: Playability | null;
  /** The instance as /my-cards sent it, for the shared Play modal. */
  instance: CardInstance;
}

/** Effect types the hand handles itself. */
export const SECOND_GUESS_EFFECT = "aotd_quiz_second_guess";
export const RECLAIM_EFFECT = "unlock_used_emoji";

/**
 * "acts:" in plain words, from the server's honest lifetime label. The API
 * has no per-card timing line, so this reads `effectLifetime`.
 */
export function actsLine(def: CardDefinition): string {
  const life = def.effectLifetime;
  if (!life) return "now";
  if (life.kind === "instant" && /^instant$/i.test(life.label)) return "now";
  if (life.kind === "fixed") return `now, for ${life.label}`;
  const label = life.label.trim();
  return label ? label.charAt(0).toLowerCase() + label.slice(1) : "now";
}

export function faceOf(def: CardDefinition): HandCardDef {
  return {
    slug: def.slug ?? def.id,
    emoji: def.emoji,
    name: def.name,
    rarity: def.rarity,
    text: def.description,
    when: actsLine(def),
  };
}

/**
 * Every in_hand copy, oldest first. Game cards (agent influence roles) are
 * not part of the hand: the server leaves `source: "game"` copies out of the
 * hand of 4 (modules/cards/services/hand.ts `handFilter`), so they stay out
 * here too. Copies take their definition and playability from their card's
 * tile.
 */
export function handFrom(data: Pick<GetMyCardsResponse, "cards" | "tiles"> | undefined): HandEntry[] {
  if (!data) return [];
  const tiles = new Map<string, OwnedCardTile>();
  (data.tiles ?? []).forEach((t) => tiles.set(t.cardId, t));
  return (data.cards ?? [])
    .filter((c) => c.status === "in_hand" && c.source !== "game")
    .map((c): HandEntry | null => {
      const tile = tiles.get(c.cardId);
      const definition = c.definition ?? tile?.definition ?? null;
      if (!definition) return null;
      if (tile && isGameCard(tile)) return null;
      if (definition.effect?.type === "agent_influence") return null;
      return {
        instanceId: c._id,
        card: faceOf(definition),
        definition,
        playability: tile?.playability ?? null,
        instance: c,
      };
    })
    .filter((e): e is HandEntry => e !== null)
    .sort((a, b) => Date.parse(a.instance.acquiredAt) - Date.parse(b.instance.acquiredAt));
}

/**
 * Keeps each card in the slot it already had, so a refetch never shuffles the
 * fan and a played card's slot keeps its stamp. New cards take the first free
 * slot that has no stamp, then any free slot. Returns DECK_SIZE slots, more
 * only when the hand is over the limit.
 */
export function assignSlots(
  prev: (string | null)[],
  ids: string[],
  stamped: ReadonlySet<number> = new Set(),
): (string | null)[] {
  const size = Math.max(DECK_SIZE, ids.length);
  const want = new Set(ids);
  const slots: (string | null)[] = Array.from({ length: size }, (_, i) => {
    const id = prev[i] ?? null;
    return id && want.has(id) ? id : null;
  });
  const placed = new Set(slots.filter(Boolean));
  const free = slots
    .map((id, i) => (id ? -1 : i))
    .filter((i) => i >= 0)
    .sort((a, b) => Number(stamped.has(a)) - Number(stamped.has(b)) || a - b);
  ids.filter((id) => !placed.has(id)).forEach((id, k) => {
    if (k < free.length) slots[free[k]] = id;
  });
  return slots;
}

/** The play affordance the hand shows for one card. */
export type HandAction =
  | { kind: "play" }
  | { kind: "target" }
  | { kind: "select"; max: number }
  | { kind: "blocked"; code: string | undefined; until: string | undefined }
  | { kind: "none" };

export function handAction(entry: HandEntry): HandAction {
  const p = entry.playability;
  if (!p) return { kind: "none" };
  switch (p.state) {
    case "ready":
    case "play_before":
      return { kind: "play" };
    case "needs_target":
      return { kind: "target" };
    case "needs_selection":
      return { kind: "select", max: p.maxSelections ?? 10 };
    case "blocked":
      return { kind: "blocked", code: p.code, until: p.until };
    default:
      return { kind: "none" };
  }
}

/**
 * Whether the hand marks a card "play now": the server says it can be played
 * (ready, play_before, needs_target, needs_selection) and the window is open
 * by the client's clock, so a stale read never marks a card after the close.
 */
export function playableNow(entry: HandEntry, windowOpen: boolean): boolean {
  if (!windowOpen) return false;
  const kind = handAction(entry).kind;
  return kind === "play" || kind === "target" || kind === "select";
}

/** The hand's element id on the AOTD page, for the waiting screens' cards button. */
export const AOTD_HAND_ID = "aotd-cards-hand";
